// frontend/src/widgets/WidgetSurface.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-07-30
// Updated:      2026-07-30
//
// Description:  Shared, non-mutating surface API for retrieving widgets that are registered for
//               a specific configurable page and validating page/widget compatibility.
//
// Change Summary (2026-07-30):
//   - Introduced the canonical access path used by pages and AddWidgetModal.
//   - Prevents pages from independently filtering or mutating WidgetRegistry.
//   - Normalises transitional legacy ids before registry lookup.
//
// Operational Notes:
//   - A surface is a presentation allow-list such as "clients", "jobs", or "opportunities".
//   - Surface membership does not represent business ownership or authorisation.
//   - Permission/licence filtering may be layered above this helper but must not rewrite registry data.

import {
  getWidgetById,
  getWidgetLayoutDefaults,
  getWidgetsForSurface,
  isWidgetAllowedOnSurface,
} from "./WidgetRegistry";
import { migrateWidgetId } from "./WidgetMigration";

export const getSurfaceWidgets = (surfaceName, options = {}) =>
  getWidgetsForSurface(surfaceName, options);

export const getSurfaceWidget = (surfaceName, widgetId) => {
  const canonicalId = migrateWidgetId(widgetId);
  if (!isWidgetAllowedOnSurface(canonicalId, surfaceName)) return null;
  return getWidgetById(canonicalId);
};

export const getSurfaceWidgetIds = (surfaceName, options = {}) =>
  getSurfaceWidgets(surfaceName, options).map((widget) => widget.id);

export const getSurfaceCategories = (surfaceName) =>
  Array.from(new Set(getSurfaceWidgets(surfaceName).map((widget) => widget.category))).sort();

export const isWidgetAvailableOnSurface = (surfaceName, widgetId) =>
  isWidgetAllowedOnSurface(migrateWidgetId(widgetId), surfaceName);

export const createSurfaceLayoutItem = (surfaceName, widgetId, position = {}) => {
  const canonicalId = migrateWidgetId(widgetId);

  if (!isWidgetAllowedOnSurface(canonicalId, surfaceName)) {
    throw new Error(`Widget "${canonicalId}" is not registered for surface "${surfaceName}".`);
  }

  return {
    i: canonicalId,
    x: Number.isFinite(position.x) ? position.x : 0,
    y: Number.isFinite(position.y) ? position.y : 0,
    ...getWidgetLayoutDefaults(canonicalId),
    ...position,
    i: canonicalId,
  };
};
